import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { ReactNode } from 'react';

import type { StateColorName } from '../../../theme/tokens/stateColors';

export type ContractCardStatus = {
  label: string;
  color: StateColorName;
};

export type ContractCardProps = {
  title: string;
  /** Workflow state of the contract term, computed by the consumer (e.g. "Échéance proche"). */
  status?: ContractCardStatus | null;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  /** Renewal terms shown under the end date, e.g. "Reconduction tacite tous les 2 ans". */
  renewalLabel?: string | null;
  noticePeriod?: string | null;
  /** Free-form pricing (amounts, frequency), kept on several lines. */
  price?: string | null;
  /** Key information as sanitized HTML. */
  description?: string | null;
  documentUrl?: string | null;
  documentLabel?: string;
  /** Secondary controls (resource menu) pinned to the top-right corner. */
  action?: ReactNode;
  'data-testid'?: string;
};

const DATE_FORMAT = 'd MMMM yyyy';
const EMPTY_VALUE = '—';

export function formatContractDate(value: Date | string | null | undefined): string {
  if (!value) {
    return EMPTY_VALUE;
  }

  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return EMPTY_VALUE;
  }

  return format(date, DATE_FORMAT, { locale: fr });
}

function hasText(value: string | null | undefined): value is string {
  return Boolean(value && value.trim().length > 0);
}

export function useContractCard(props: ContractCardProps) {
  return {
    formattedStartDate: formatContractDate(props.startDate),
    formattedEndDate: formatContractDate(props.endDate),
    hasNoticePeriod: hasText(props.noticePeriod),
    hasPrice: hasText(props.price),
    hasDescription: hasText(props.description),
    documentLabel: props.documentLabel ?? 'Contrat',
  };
}
